import { Textarea } from "@/components/ui/textarea"
import { SleepStepContainer } from "../SleepStepContainer"
import { useSleepForm } from "../SleepFormContext"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { FileText } from "lucide-react"

interface NotesStepProps {
  childName?: string
}

export function NotesStep({ childName }: NotesStepProps) {
  const form = useSleepForm()
  
  const sleepSuggestions = [
    "Sleep cues observed",
    "Any disruptions during sleep",
    "Mood before and after sleep",
    "Changes to normal routine",
    "Environment factors that might have affected sleep",
    "Position baby slept in"
  ]
  
  const handleUseTemplate = () => {
    const template = sleepSuggestions.map(s => `• ${s}:\n`).join('');
    form.setValue("notes", template);
  }

  return (
    <SleepStepContainer>
      <div className="space-y-2 mt-4">
        <div className="flex items-center justify-center space-x-2 mb-2">
          <FileText className="h-5 w-5 text-primary" />
          <h3 className="text-lg font-medium text-center">
            Any notes about {childName || 'your baby\'s'} sleep?
          </h3>
        </div>
        
        <div className="max-w-xl mx-auto space-y-3">
          <Card className="border border-input">
            <CardContent className="p-4 space-y-4">
              <Textarea
                placeholder={`Add any notes about this sleep session...`}
                {...form.register("notes")}
                className="min-h-[120px] border-0 focus-visible:ring-0 focus-visible:ring-offset-0 p-0 shadow-none resize-y"
                onKeyDown={(e) => {
                  // Prevent form submission on Enter key
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                  }
                }}
              />
              
              <div className="text-xs text-muted-foreground space-y-2">
                <p>Press Shift + Enter for new line</p>
                
                <div className="pt-2 border-t border-border">
                  <p className="font-medium">Suggested topics:</p>
                  <ul className="mt-1 space-y-1 grid grid-cols-1 sm:grid-cols-2 gap-x-4">
                    {sleepSuggestions.map((suggestion, index) => (
                      <li key={index} className="flex items-start">
                        <span className="mr-1.5">•</span>
                        <span>{suggestion}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                
                <div className="pt-2 border-t border-border mt-2 flex items-center gap-1 justify-center">
                  <Button 
                    variant="outline" 
                    size="sm"
                    className="h-6 text-xs rounded-sm px-2"
                    onClick={handleUseTemplate}
                    type="button"
                  >
                    Use Template
                  </Button>
                  <p className="text-xs">Click to insert a formatted template with these topics</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </SleepStepContainer>
  )
}